import type {
  CheckpointStatus,
  VerificationMethod,
} from "../../shared/src/checkpoint-verification.ts";
import type { QrCaptureLocation } from "./qr-capture.ts";

export const ROUTE_ASSIGNMENT_COLUMNS = "id, route_id, starts_at, ends_at, status";
export const ROUTE_CHECKPOINT_COLUMNS = "id, checkpoint_id, sort_order, is_required";
export const CHECKPOINT_DETAIL_COLUMNS =
  "id, name, active, verification_status, preferred_verification_method, allowed_fallback_methods, expected_lat, expected_lng";
export const GUARD_ROUTE_CHECKPOINT_CANDIDATE_COLUMNS =
  "route_assignment_id, route_assignment_status, starts_at, ends_at, route_id, route_name, route_checkpoint_id, checkpoint_id, checkpoint_name, sort_order, is_required, checkpoint_active, verification_status, preferred_verification_method, allowed_fallback_methods, expected_lat, expected_lng";

export interface MobileRouteAssignment {
  endsAt: string | null;
  id: string;
  routeId: string;
  startsAt: string;
  status: string;
}

export interface MobileRouteCheckpointCandidate {
  active: boolean;
  allowedFallbackMethods: readonly VerificationMethod[];
  checkpointId: string;
  distanceMeters: number | null;
  expectedLat: number | null;
  expectedLng: number | null;
  isRequired: boolean;
  manualFallbackReady: boolean;
  name: string;
  operationalStatus: CheckpointStatus;
  preferredVerificationMethod: VerificationMethod;
  routeCheckpointId: string;
  sortOrder: number;
}

export type GuardRouteAssignment = MobileRouteAssignment;
export type GuardRouteCheckpointCandidate = MobileRouteCheckpointCandidate;

/** A checkpoint that can honestly appear in route progress and pin pickers. */
export function isUsableRouteCheckpointCandidate(candidate: MobileRouteCheckpointCandidate): boolean {
  return candidate.active && candidate.operationalStatus !== "disabled";
}

export interface GuardRouteCheckpointSnapshot {
  assignment: MobileRouteAssignment | null;
  checkpoints: MobileRouteCheckpointCandidate[];
}

export interface RouteAssignmentRow {
  ends_at: unknown;
  id: unknown;
  route_id: unknown;
  starts_at: unknown;
  status: unknown;
}

export interface RouteCheckpointRow {
  checkpoint_id: unknown;
  id: unknown;
  is_required: unknown;
  sort_order: unknown;
}

export interface CheckpointDetailRow {
  active: unknown;
  allowed_fallback_methods: unknown;
  expected_lat: unknown;
  expected_lng: unknown;
  id: unknown;
  name: unknown;
  preferred_verification_method: unknown;
  verification_status: unknown;
}

export interface GuardRouteCheckpointCandidateRow {
  allowed_fallback_methods: unknown;
  checkpoint_active: unknown;
  checkpoint_id: unknown;
  checkpoint_name: unknown;
  ends_at: unknown;
  expected_lat: unknown;
  expected_lng: unknown;
  is_required: unknown;
  preferred_verification_method: unknown;
  route_assignment_id: unknown;
  route_assignment_status: unknown;
  route_checkpoint_id: unknown;
  route_id: unknown;
  route_name: unknown;
  sort_order: unknown;
  starts_at: unknown;
  verification_status: unknown;
}

export interface SupabaseQuery<T> extends PromiseLike<{ data: T[] | null; error: { message: string } | null }> {
  eq(column: string, value: unknown): SupabaseQuery<T>;
  in(column: string, values: unknown[]): SupabaseQuery<T>;
  limit(count: number): SupabaseQuery<T>;
  order(column: string, options: { ascending: boolean }): SupabaseQuery<T>;
  select(columns: string): SupabaseQuery<T>;
}

export interface SupabaseRouteCheckpointClient {
  from<T>(
    table: "route_assignments" | "route_checkpoints" | "checkpoints" | "guard_route_checkpoint_candidates",
  ): SupabaseQuery<T>;
}

export interface RouteCheckpointSourceOptions {
  activeAccountId: string;
}

export function createSupabaseRouteCheckpointSource(
  client: SupabaseRouteCheckpointClient,
  options: RouteCheckpointSourceOptions,
) {
  return {
    async loadLatestAssignmentForGuard(guardUserId: string): Promise<MobileRouteAssignment | null> {
      const { data, error } = await client
        .from<RouteAssignmentRow>("route_assignments")
        .select(ROUTE_ASSIGNMENT_COLUMNS)
        .eq("account_id", options.activeAccountId)
        .eq("guard_user_id", guardUserId)
        .in("status", ["active", "scheduled"])
        .order("starts_at", { ascending: false })
        .limit(1);

      if (error) {
        throw new Error(error.message);
      }

      const [row] = data ?? [];
      return row ? toRouteAssignment(row) : null;
    },

    async loadCheckpointCandidates(routeId: string): Promise<MobileRouteCheckpointCandidate[]> {
      const { data: routeRows, error: routeError } = await client
        .from<RouteCheckpointRow>("route_checkpoints")
        .select(ROUTE_CHECKPOINT_COLUMNS)
        .eq("account_id", options.activeAccountId)
        .eq("route_id", routeId)
        .order("sort_order", { ascending: true });

      if (routeError) {
        throw new Error(routeError.message);
      }

      const checkpointIds = [...new Set((routeRows ?? []).map((row) => requiredString(row.checkpoint_id, "checkpoint_id")))];
      if (checkpointIds.length === 0) {
        return [];
      }

      const { data: checkpointRows, error: checkpointError } = await client
        .from<CheckpointDetailRow>("checkpoints")
        .select(CHECKPOINT_DETAIL_COLUMNS)
        .eq("account_id", options.activeAccountId)
        .in("id", checkpointIds);

      if (checkpointError) {
        throw new Error(checkpointError.message);
      }

      return toRouteCheckpointCandidates(routeRows ?? [], checkpointRows ?? []);
    },
  };
}

export function createSupabaseGuardRouteCheckpointCandidateSource(
  client: SupabaseRouteCheckpointClient,
  options: RouteCheckpointSourceOptions,
) {
  return {
    async loadCurrentRouteCheckpointCandidates(): Promise<GuardRouteCheckpointSnapshot> {
      const { data, error } = await client
        .from<GuardRouteCheckpointCandidateRow>("guard_route_checkpoint_candidates")
        .select(GUARD_ROUTE_CHECKPOINT_CANDIDATE_COLUMNS)
        .eq("account_id", options.activeAccountId)
        .order("starts_at", { ascending: false })
        .order("sort_order", { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      return toGuardRouteCheckpointSnapshot(data ?? []);
    },
  };
}

export function toGuardRouteCheckpointSnapshot(
  rows: readonly GuardRouteCheckpointCandidateRow[],
): GuardRouteCheckpointSnapshot {
  if (rows.length === 0) {
    return { assignment: null, checkpoints: [] };
  }

  const sortedRows = [...rows].sort(compareGuardRouteCheckpointRows);
  const [firstRow] = sortedRows;
  const assignmentId = requiredString(firstRow?.route_assignment_id, "route_assignment_id");
  const assignmentRows = sortedRows.filter(
    (row) => requiredString(row.route_assignment_id, "route_assignment_id") === assignmentId,
  );

  return {
    assignment: {
      endsAt: firstRow?.ends_at === null ? null : requiredString(firstRow?.ends_at, "ends_at"),
      id: assignmentId,
      routeId: requiredString(firstRow?.route_id, "route_id"),
      startsAt: requiredString(firstRow?.starts_at, "starts_at"),
      status: requiredString(firstRow?.route_assignment_status, "route_assignment_status"),
    },
    checkpoints: assignmentRows.map(toRouteCheckpointCandidateFromViewRow).sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

export function toRouteCheckpointCandidates(
  routeRows: readonly RouteCheckpointRow[],
  checkpointRows: readonly CheckpointDetailRow[],
): MobileRouteCheckpointCandidate[] {
  const checkpointsById = new Map(checkpointRows.map((row) => [requiredString(row.id, "checkpoint.id"), row]));

  return routeRows
    .map((routeRow) => {
      const checkpointId = requiredString(routeRow.checkpoint_id, "checkpoint_id");
      const checkpoint = checkpointsById.get(checkpointId);
      if (!checkpoint) {
        throw new Error(`Route checkpoint is missing checkpoint details for ${checkpointId}.`);
      }

      const preferredVerificationMethod = toVerificationMethod(
        checkpoint.preferred_verification_method,
        "preferred_verification_method",
      );
      const allowedFallbackMethods = toVerificationMethods(checkpoint.allowed_fallback_methods);
      const operationalStatus = toCheckpointStatus(checkpoint.verification_status);
      const active = toBoolean(checkpoint.active, "checkpoint.active");

      return {
        active,
        allowedFallbackMethods,
        checkpointId,
        distanceMeters: null,
        expectedLat: toNullableNumber(checkpoint.expected_lat, "expected_lat"),
        expectedLng: toNullableNumber(checkpoint.expected_lng, "expected_lng"),
        isRequired: toBoolean(routeRow.is_required, "is_required"),
        manualFallbackReady:
          active &&
          operationalStatus === "active" &&
          canUseManualFallback(preferredVerificationMethod, allowedFallbackMethods),
        name: requiredString(checkpoint.name, "checkpoint.name"),
        operationalStatus,
        preferredVerificationMethod,
        routeCheckpointId: requiredString(routeRow.id, "route_checkpoint.id"),
        sortOrder: toNumber(routeRow.sort_order, "sort_order"),
      };
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function toRouteCheckpointCandidateFromViewRow(
  row: GuardRouteCheckpointCandidateRow,
): MobileRouteCheckpointCandidate {
  const preferredVerificationMethod = toVerificationMethod(
    row.preferred_verification_method,
    "preferred_verification_method",
  );
  const allowedFallbackMethods = toVerificationMethods(row.allowed_fallback_methods);
  const operationalStatus = toCheckpointStatus(row.verification_status);
  const active = toBoolean(row.checkpoint_active, "checkpoint_active");

  return {
    active,
    allowedFallbackMethods,
    checkpointId: requiredString(row.checkpoint_id, "checkpoint_id"),
    distanceMeters: null,
    expectedLat: toNullableNumber(row.expected_lat, "expected_lat"),
    expectedLng: toNullableNumber(row.expected_lng, "expected_lng"),
    isRequired: toBoolean(row.is_required, "is_required"),
    manualFallbackReady:
      active &&
      operationalStatus === "active" &&
      canUseManualFallback(preferredVerificationMethod, allowedFallbackMethods),
    name: requiredString(row.checkpoint_name, "checkpoint_name"),
    operationalStatus,
    preferredVerificationMethod,
    routeCheckpointId: requiredString(row.route_checkpoint_id, "route_checkpoint_id"),
    sortOrder: toNumber(row.sort_order, "sort_order"),
  };
}

export function rankManualFallbackCandidates(
  candidates: readonly MobileRouteCheckpointCandidate[],
  location: QrCaptureLocation,
): MobileRouteCheckpointCandidate[] {
  return candidates
    .map((candidate) => ({
      ...candidate,
      distanceMeters: distanceFromLocation(candidate, location),
    }))
    .sort((a, b) => {
      if (a.distanceMeters !== null && b.distanceMeters !== null) {
        return a.distanceMeters - b.distanceMeters;
      }
      if (a.distanceMeters !== null) {
        return -1;
      }
      if (b.distanceMeters !== null) {
        return 1;
      }
      return a.sortOrder - b.sortOrder;
    });
}

function toRouteAssignment(row: RouteAssignmentRow): MobileRouteAssignment {
  const endsAt = row.ends_at === null ? null : requiredString(row.ends_at, "ends_at");
  return {
    endsAt,
    id: requiredString(row.id, "route_assignment.id"),
    routeId: requiredString(row.route_id, "route_id"),
    startsAt: requiredString(row.starts_at, "starts_at"),
    status: requiredString(row.status, "status"),
  };
}

function compareGuardRouteCheckpointRows(
  left: GuardRouteCheckpointCandidateRow,
  right: GuardRouteCheckpointCandidateRow,
): number {
  const rightStartsAt = requiredString(right.starts_at, "starts_at");
  const leftStartsAt = requiredString(left.starts_at, "starts_at");
  const assignmentOrder = rightStartsAt.localeCompare(leftStartsAt);
  if (assignmentOrder !== 0) {
    return assignmentOrder;
  }
  return toNumber(left.sort_order, "sort_order") - toNumber(right.sort_order, "sort_order");
}

function canUseManualFallback(
  preferredVerificationMethod: VerificationMethod,
  allowedFallbackMethods: readonly VerificationMethod[],
): boolean {
  return (
    preferredVerificationMethod === "gps_geofence" ||
    preferredVerificationMethod === "manual" ||
    allowedFallbackMethods.includes("gps_geofence") ||
    allowedFallbackMethods.includes("manual")
  );
}

function distanceFromLocation(candidate: MobileRouteCheckpointCandidate, location: QrCaptureLocation): number | null {
  if (
    location.permission !== "granted" ||
    location.lat === null ||
    location.lng === null ||
    candidate.expectedLat === null ||
    candidate.expectedLng === null
  ) {
    return null;
  }

  return haversineMeters(location.lat, location.lng, candidate.expectedLat, candidate.expectedLng);
}

function haversineMeters(latA: number, lngA: number, latB: number, lngB: number): number {
  const earthRadiusMeters = 6_371_000;
  const dLat = radians(latB - latA);
  const dLng = radians(lngB - lngA);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(radians(latA)) * Math.cos(radians(latB)) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadiusMeters * Math.asin(Math.sqrt(a));
}

function radians(value: number): number {
  return (value * Math.PI) / 180;
}

function toVerificationMethods(value: unknown): VerificationMethod[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map((method) => toVerificationMethod(method, "allowed_fallback_methods"));
}

function toVerificationMethod(value: unknown, field: string): VerificationMethod {
  if (value === "nfc" || value === "qr" || value === "gps_geofence" || value === "manual") {
    return value;
  }
  throw new Error(`Invalid ${field}.`);
}

function toCheckpointStatus(value: unknown): CheckpointStatus {
  if (value === "active" || value === "maintenance" || value === "disabled") {
    return value;
  }
  throw new Error("Invalid checkpoint operational status.");
}

function requiredString(value: unknown, field: string): string {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    throw new Error(`Missing ${field}.`);
  }
  return trimmed;
}

function toBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") {
    throw new Error(`Invalid ${field}.`);
  }
  return value;
}

function toNumber(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Invalid ${field}.`);
  }
  return value;
}

function toNullableNumber(value: unknown, field: string): number | null {
  if (value === null) {
    return null;
  }
  return toNumber(value, field);
}
